// Shared recurrence helpers — UI labels map to CANONICAL backend values.
// No duplicate frequency meanings are stored:
//   Weekly=WEEKLY/1, Fortnightly=WEEKLY/2, Monthly=MONTHLY/1, Quarterly=MONTHLY/3, One-off=ONE_OFF

export const FREQUENCY_OPTIONS = ['Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'One-off'];

export const WEEKDAYS = [
  { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

export const NTH_OPTIONS = [
  { value: 1, label: 'First' }, { value: 2, label: 'Second' }, { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' }, { value: 5, label: 'Fifth' }, { value: -1, label: 'Last' },
];

export function toCanonical(uiFreq) {
  switch (uiFreq) {
    case 'Weekly': return { frequency: 'WEEKLY', interval: 1 };
    case 'Fortnightly': return { frequency: 'WEEKLY', interval: 2 };
    case 'Monthly': return { frequency: 'MONTHLY', interval: 1 };
    case 'Quarterly': return { frequency: 'MONTHLY', interval: 3 };
    case 'One-off': return { frequency: 'ONE_OFF', interval: 1 };
    default: return { frequency: 'WEEKLY', interval: 1 };
  }
}

export function labelFromCanonical(frequency, interval) {
  if (frequency === 'ONE_OFF') return 'One-off';
  if (frequency === 'WEEKLY') return interval === 2 ? 'Fortnightly' : 'Weekly';
  if (frequency === 'MONTHLY') return interval === 3 ? 'Quarterly' : 'Monthly';
  return frequency;
}

const isWeekly = (uiFreq) => uiFreq === 'Weekly' || uiFreq === 'Fortnightly';
const isMonthly = (uiFreq) => uiFreq === 'Monthly' || uiFreq === 'Quarterly';

// Build the recurrence portion of a service-item create/update payload.
export function buildRulePayload(form) {
  const { frequency, interval } = toCanonical(form.uiFreq);
  const payload = {
    frequency,
    interval,
    start_time: form.start_time,
    end_time: form.end_time,
  };
  if (frequency === 'ONE_OFF') {
    payload.one_off_date = form.one_off_date || null;
    return payload;
  }
  payload.recurrence_start_date = form.recurrence_start_date || null;
  if (form.recurrence_end_date) payload.recurrence_end_date = form.recurrence_end_date;
  if (isWeekly(form.uiFreq)) {
    payload.days_of_week = (form.days_of_week && form.days_of_week.length)
      ? [...form.days_of_week].sort((a, b) => a - b)
      : [];
    // keep legacy day_of_week aligned to the first selected weekday
    payload.day_of_week = payload.days_of_week.length ? payload.days_of_week[0] : 0;
  }
  if (isMonthly(form.uiFreq)) {
    payload.monthly_mode = form.monthly_mode || 'DAY_OF_MONTH';
    if (payload.monthly_mode === 'DAY_OF_MONTH') {
      payload.day_of_month = parseInt(form.day_of_month, 10) || 1;
    } else {
      payload.nth_weekday = {
        nth: parseInt(form.nth_n, 10),
        weekday: parseInt(form.nth_weekday, 10),
      };
    }
  }
  return payload;
}

// Client-side guard mirroring the server's structured validation.
export function validateRuleForm(form) {
  if (form.uiFreq === 'One-off') {
    if (!form.one_off_date) return 'Please choose the one-off date';
    return null;
  }
  if (!form.recurrence_start_date) return 'Recurrence start date is required';
  if (isWeekly(form.uiFreq) && !(form.days_of_week && form.days_of_week.length)) {
    return 'Select at least one weekday';
  }
  if (isMonthly(form.uiFreq) && (form.monthly_mode || 'DAY_OF_MONTH') === 'NTH_WEEKDAY'
      && (form.nth_n === undefined || form.nth_n === '' || form.nth_weekday === undefined || form.nth_weekday === '')) {
    return 'Choose the nth weekday (e.g. Second Tuesday)';
  }
  return null;
}

// Build the generate-shifts request body from the popover form.
export const GENERATED_SHIFT_RATIOS = [
  { value: '1:1', label: '1:1 — one worker, one participant' },
  { value: '1:2', label: '1:2 — shared, one worker, two participants' },
  { value: '1:3', label: '1:3 — shared, one worker, three participants' },
  { value: '2:1', label: '2:1 — two workers, one participant' },
];

// Ratio is always sent explicitly so the backend default (1:1) never applies silently.
export function buildGeneratePayload(gen) {
  const support_ratio = gen.support_ratio || '1:1';
  if (gen.mode === 'RANGE') return { mode: 'RANGE', from_date: gen.from_date, to_date: gen.to_date, support_ratio };
  if (gen.mode === 'COUNT') {
    const body = { mode: 'COUNT', count: parseInt(gen.count, 10), support_ratio };
    if (gen.from_date) body.from_date = gen.from_date;
    return body;
  }
  return { mode: 'PRESET', preset: gen.preset || 'NEXT_4_WEEKS', support_ratio };
}

export function validateGenerateForm(gen) {
  if (gen.mode === 'RANGE') {
    if (!gen.from_date || !gen.to_date) return 'Choose both from and to dates';
    if (gen.to_date < gen.from_date) return 'To date must be on or after from date';
  }
  if (gen.mode === 'COUNT') {
    const c = parseInt(gen.count, 10);
    if (!c || c < 1) return 'Enter a positive occurrence count';
    if (c > 100) return 'Count must be 100 or fewer';
  }
  return null;
}
